import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: EASE, delay: 0.25 + i * 0.14 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const scrollTo = (target) => window.__lenis?.scrollTo(target, { offset: -64, duration: 1.6 });

  return (
    <section id="top" ref={ref} data-testid="hero" className="ruled-lines relative min-h-screen pt-14 overflow-hidden">
      <motion.div
        style={{ y: titleY }}
        className="flex flex-col justify-center min-h-[calc(100vh-3.5rem)] px-4 sm:px-8 py-16 lg:py-0"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="font-mono text-[10px] sm:text-xs tracking-[0.24em] uppercase text-ink2 mb-6"
          data-testid="hero-kicker"
        >
          Field Report No. 001 — Summer 2026 · Austin, Texas
        </motion.p>

        <h1 className="font-serif font-semibold tracking-tight leading-[0.88] text-[17vw] sm:text-[13vw] lg:text-[8.5rem] xl:text-[10rem]">
          {["THE AUSTIN", "TACO", "INDEX"].map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="show"
                className={`block ${i === 1 ? "italic text-salsa font-medium" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
          className="mt-8 max-w-xl font-body italic text-base md:text-lg text-ink2"
          data-testid="hero-deck"
        >
          Authenticity, salsa, and the business of the perfect taco — a composite-scored field study
          of 17 taquerias, cross-verified against Google, Yelp, and the Infatuation.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[10px] sm:text-xs tracking-[0.18em] uppercase"
          data-testid="hero-stats"
        >
          <span>17 Spots</span>
          <span className="text-salsa">/</span>
          <span>51 Tacos Scored</span>
          <span className="text-salsa">/</span>
          <span>3 Platforms Checked</span>
          <span className="text-salsa">/</span>
          <span>1 Standard</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.8, ease: EASE }}
          className="no-print mt-10 flex flex-wrap gap-3"
        >
          <button
            data-testid="hero-read-index-btn"
            onClick={() => scrollTo("#the-index")}
            className="group inline-flex items-center gap-2 border border-ink bg-ink text-paper px-5 py-3 font-mono text-[11px] tracking-[0.16em] uppercase hover:bg-salsa hover:border-salsa transition-colors"
          >
            Read the Index <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-0.5" />
          </button>
          <button
            data-testid="hero-map-btn"
            onClick={() => scrollTo("#the-map")}
            className="inline-flex items-center gap-2 border border-ink px-5 py-3 font-mono text-[11px] tracking-[0.16em] uppercase hover:bg-ink hover:text-paper transition-colors"
          >
            <MapPin size={14} /> Skip to Map
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-5 left-4 sm:left-8 flex items-center gap-3 font-mono text-[10px] tracking-[0.24em] uppercase text-muted2"
      >
        Scroll
        <motion.span
          animate={{ scaleY: [0, 1, 1, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="block w-px h-8 bg-ink origin-top"
        />
      </motion.div>
    </section>
  );
};

export default Hero;
